import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import FilmCard from "@/components/feature/FilmCard";
import usePageMeta from "@/hooks/usePageMeta";
import {
  ACTOR_PROFILES,
  CATEGORY_META,
  FILMS,
  getCategoryName,
  getFilmById,
} from "@/mocks/films";

export default function DetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [copiedToast, setCopiedToast] = useState(false);

  const film = getFilmById(id);

  usePageMeta({
    title: film ? `${film.title} - 影片详情 - 欧美成人电影` : "影片不存在 - 欧美成人电影",
    description: film
      ? `${film.title}（${film.titleEn}，${film.year}，${film.region}）剧情简介：${film.summary} 主演 ${film.cast.join("、")}，导演 ${film.director}。`
      : "影片不存在或已下架，返回首页浏览更多欧美成人电影资料。",
    keywords: film
      ? `${film.title},${film.titleEn},欧美成人电影,${film.cast.join(",")},${getCategoryName(film.categories[0])}`
      : "欧美成人电影,影片详情",
    canonicalPath: film ? `/detail/${film.id}` : "/detail",
  });

  const related = useMemo(() => {
    if (!film) return [];
    return FILMS.filter(
      (item) =>
        item.id !== film.id &&
        (item.categories.some((slug) => film.categories.includes(slug)) ||
          item.tags.some((tag) => film.tags.includes(tag))),
    ).slice(0, 4);
  }, [film]);

  if (!film) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 py-20 md:py-28">
          <div className="max-w-xl mx-auto text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-2xl">
              <i className="ri-closed-captioning-line" />
            </span>
            <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
              影片不存在或已下架
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              该影片资料可能已调整路径或被移除，你可以返回首页继续浏览其他影片。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link
                to="/"
                className="h-10 px-5 rounded-md bg-primary-900 text-background-50 text-sm font-medium flex items-center whitespace-nowrap hover:bg-primary-800 transition-colors"
              >
                返回首页
              </Link>
              <Link
                to="/category/drama"
                className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm flex items-center whitespace-nowrap hover:border-accent-400 transition-colors"
              >
                浏览剧情分类
              </Link>
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopiedToast(true);
      window.setTimeout(() => setCopied(false), 2200);
      window.setTimeout(() => setCopiedToast(false), 2600);
    } catch {
      setCopiedToast(true);
      window.setTimeout(() => setCopiedToast(false), 2600);
    }
  };

  const actorList = film.cast.map((name) => ({
    name,
    profile: ACTOR_PROFILES[name],
  }));

  return (
    <SiteLayout>
      <div className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="max-w-[1280px] mx-auto flex items-center gap-1.5 text-[12px] text-background-400 flex-wrap">
            <Link to="/" className="hover:text-accent-300 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-[11px]" />
            <Link to={`/category/${film.categories[0]}`} className="hover:text-accent-300 transition-colors">
              {getCategoryName(film.categories[0])}
            </Link>
            <i className="ri-arrow-right-s-line text-[11px]" />
            <span className="text-background-200">{film.title}</span>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-[1280px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] xl:grid-cols-[360px_1fr] gap-6 lg:gap-10">
            <div>
              <div className="w-full aspect-[3/4] rounded-lg overflow-hidden border border-background-200 bg-background-200">
                <img
                  src={film.cover}
                  alt={`${film.title} 影片详情海报`}
                  title={`${film.title} · 影片详情`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {[
                  { label: "评分", value: film.rating.toFixed(1), icon: "ri-star-fill" },
                  { label: "时长", value: `${film.duration}′`, icon: "ri-time-line" },
                  { label: "浏览", value: `${(film.views / 1000).toFixed(1)}k`, icon: "ri-eye-line" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-md border border-background-200 bg-background-100 px-2 py-2.5 text-center"
                  >
                    <i className={`${item.icon} text-[11px] text-accent-600`} />
                    <p className="mt-1 font-heading text-sm font-semibold text-foreground-950">
                      {item.value}
                    </p>
                    <p className="text-[11px] text-foreground-500">{item.label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-lg border border-background-200 p-4">
                <p className="text-[12px] text-foreground-500 mb-2.5 flex items-center gap-1.5">
                  <i className="ri-share-forward-line text-accent-700" />
                  分享此影片页
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={copyLink}
                    className={`flex-1 h-9 rounded-md text-[12px] whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-1.5 ${
                      copied
                        ? "bg-accent-100 text-accent-800 border border-accent-300"
                        : "bg-primary-900 text-background-50 hover:bg-primary-800"
                    }`}
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
                    {copied ? "链接已复制" : "复制链接"}
                  </button>
                  {["ri-twitter-x-line", "ri-facebook-circle-line", "ri-mail-line"].map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      aria-label="演示用分享按钮"
                      onClick={() => {
                        setCopiedToast(true);
                        window.setTimeout(() => setCopiedToast(false), 2400);
                      }}
                      className="w-9 h-9 rounded-md border border-background-300 text-foreground-500 flex items-center justify-center hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer"
                    >
                      <i className={icon} />
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-foreground-400">分享功能为页面演示，不调用社交平台接口。</p>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {film.categories.map((slug) => (
                  <Link
                    key={slug}
                    to={`/category/${slug}`}
                    className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-[12px] hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    {getCategoryName(slug)}
                  </Link>
                ))}
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-[12px]">
                  {film.region}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-[12px]">
                  {film.year}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                {film.title}
              </h1>
              <p className="mt-1.5 text-[12px] tracking-[0.2em] uppercase text-foreground-400">
                {film.titleEn}
              </p>

              <div className="mt-5 rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <p className="text-[11px] tracking-wider text-foreground-400 mb-1">导演</p>
                  <p className="text-sm text-foreground-900">{film.director}</p>
                </div>
                <div>
                  <p className="text-[11px] tracking-wider text-foreground-400 mb-1">上映年份</p>
                  <p className="text-sm text-foreground-900">
                    {film.year} 年 · {film.releaseDate}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] tracking-wider text-foreground-400 mb-1">片长 / 地区</p>
                  <p className="text-sm text-foreground-900">
                    {film.duration} 分钟 · {film.region}
                  </p>
                </div>
              </div>

              <section className="mt-7">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-4 bg-accent-500 rounded-full" />
                  剧情简介
                </h2>
                <p className="mt-3 text-[14px] leading-7 text-foreground-700">{film.summary}</p>
                <p className="mt-3 text-[14px] leading-7 text-foreground-600">{film.synopsis}</p>
              </section>

              <section className="mt-7">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-4 bg-accent-500 rounded-full" />
                  演员资料
                </h2>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {actorList.map((actor) => (
                    <Link
                      key={actor.name}
                      to={`/search?q=${encodeURIComponent(actor.name)}`}
                      className="group flex items-center gap-3 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-accent-300 transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-background-200 shrink-0">
                        {actor.profile ? (
                          <img
                            src={actor.profile.avatar}
                            alt={`${actor.name} 演员头像`}
                            title={`${actor.name} · 演员资料`}
                            className="w-full h-full object-cover object-top"
                          />
                        ) : (
                          <span className="w-full h-full flex items-center justify-center text-foreground-400">
                            <i className="ri-user-line" />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-900 group-hover:text-accent-700 transition-colors line-clamp-1">
                          {actor.name}
                        </p>
                        <p className="mt-0.5 text-[11px] text-foreground-500 line-clamp-1">
                          {actor.profile?.role ?? "演员"}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>

              <section className="mt-7">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-4 bg-accent-500 rounded-full" />
                  影片标签
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {film.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-[12px] text-foreground-600 hover:border-accent-300 hover:text-accent-800 transition-colors cursor-pointer"
                    >
                      # {tag}
                    </Link>
                  ))}
                </div>
              </section>

              <section className="mt-7">
                <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                  <span className="w-1 h-4 bg-accent-500 rounded-full" />
                  分类快捷入口
                </h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {CATEGORY_META.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="h-9 px-3.5 rounded-md border border-background-300 text-[12px] text-foreground-700 flex items-center gap-1.5 whitespace-nowrap hover:border-accent-400 hover:text-accent-800 transition-colors cursor-pointer"
                    >
                      <i className={`${cat.icon} text-accent-600`} />
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </section>
            </div>
          </div>

          <section className="mt-12 md:mt-16">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2 mb-5">
              <div>
                <p className="text-[11px] tracking-[0.3em] uppercase text-accent-700 mb-1.5">
                  You May Also Like
                </p>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  相关推荐
                </h2>
              </div>
              <p className="text-[12px] text-foreground-500">
                依据题材分类与影片标签生成 · {related.length} 部
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((item) => (
                <FilmCard key={item.id} film={item} />
              ))}
            </div>
          </section>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-3 pt-6 border-t border-background-200">
            <p className="text-[12px] text-foreground-500 max-w-2xl leading-relaxed">
              本页仅提供影片剧情、演职员与上映信息介绍，不提供任何播放或下载内容。资料如有出入欢迎通过关于页邮箱反馈。
            </p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="h-9 px-4 rounded-md border border-background-300 text-[12px] text-foreground-700 flex items-center gap-1.5 whitespace-nowrap hover:border-accent-400 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回上一页
            </button>
          </div>
        </div>
      </div>

      {copiedToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] animate-fade-up">
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-950 text-background-100 text-[13px]">
            <i className="ri-information-line text-accent-400" />
            {copied ? "影片页链接已复制到剪贴板" : "演示用分享入口，未调用社交平台接口"}
          </div>
        </div>
      )}

    </SiteLayout>
  );
}